// The modal shell, and the confirmation dialog built on it.
// Used instead of window.confirm so a destructive action is always on-brand.

import type { ReactNode } from "react";
import { IconX } from "@/platform/ui/icons.js";
import { createPortal } from "react-dom";
import { useEffect, useId, useRef, type KeyboardEvent as ReactKeyboardEvent } from "react";
import { Button } from "@/platform/ui/primitives/buttons.js";
import { cx } from "@/platform/ui/primitives/cx.js";
import { useRegisterModalOpen } from "@/platform/client";

// ---- Modal ----
export function Modal({
  open,
  onClose,
  title,
  children,
  size = "md",
}: {
  open: boolean;
  onClose: () => void;
  title: ReactNode;
  children: ReactNode;
  size?: "md" | "lg" | "xl" | "2xl";
}) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  // Lock the background in place while open. Plain `overflow: hidden` on the
  // body doesn't by itself stop touch-scroll chaining on mobile (iOS Safari
  // in particular still lets a swipe outside the panel scroll the page
  // behind), so the panel itself opts out of chaining via `overscroll-
  // contain` below. That means the lock here can stay a plain toggle — every
  // consumer keeps `Modal` mounted and only flips `open` (never unmounts it),
  // so there's no captured state to drift between opens. The `scrollTo(0, 0)`
  // is a defensive reset, not a restore: this app never scrolls `document.body`
  // for real content (AppShell's inner column is the sole intended scroll
  // owner), so there's no legitimate non-zero resting position to preserve —
  // pinning to 0 guards against Android's address-bar animation nudging the
  // page while a `fixed` panel is up.
  useEffect(() => {
    if (!open) return;
    const body = document.body;
    const prevOverflow = body.style.overflow;
    body.style.overflow = "hidden";
    window.scrollTo(0, 0);
    return () => {
      body.style.overflow = prevOverflow;
    };
  }, [open]);

  // Lets other global keyboard-shortcut listeners (useEntrySelection, the
  // Feeds page's own shortcuts) know a modal is up so they can stay quiet —
  // otherwise a keydown meant for a focused button in here also reaches
  // them, since they're bound on `window` and this doesn't stop propagation.
  useRegisterModalOpen(open);
  const titleId = useId();

  if (!open) return null;
  const maxW = { md: "max-w-lg", lg: "max-w-2xl", xl: "max-w-4xl", "2xl": "max-w-6xl" }[size];
  return createPortal(
    <div
      className="fixed inset-x-0 top-0 z-50 flex h-dvh items-end justify-center bg-black/60 p-0 sm:items-center sm:p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={cx(
          "max-h-[92dvh] w-full overflow-y-auto overscroll-contain rounded-t-2xl border border-line bg-surface p-5 shadow-2xl sm:rounded-2xl",
          maxW,
        )}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-center justify-between">
          <h2 id={titleId} className="text-base font-bold text-ink">
            {title}
          </h2>
          <button
            onClick={onClose}
            aria-label="Close"
            className="rounded-md p-1 text-muted hover:bg-surface-2 hover:text-ink"
          >
            <IconX size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>,
    document.body,
  );
}

// A small in-app confirmation dialog — used instead of the browser's native
// window.confirm so destructive actions get a consistent, on-brand prompt.
export function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = "Delete",
  tone = "danger",
  busy,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  message: ReactNode;
  confirmLabel?: string;
  /** "danger" (default): red confirm button, Cancel focused first — an Enter that
   *  lands right as the dialog appears can't trigger the destructive action.
   *  "positive": for a reversible/affirmative action, so the fast path (Enter)
   *  should confirm rather than cancel — green confirm button, focused first. */
  tone?: "danger" | "positive";
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const cancelRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);
  const focusRef = tone === "positive" ? confirmRef : cancelRef;

  useEffect(() => {
    if (open) focusRef.current?.focus();
  }, [open, tone]);

  function onArrowKey(e: ReactKeyboardEvent<HTMLDivElement>) {
    if (e.key === "ArrowLeft") {
      e.preventDefault();
      cancelRef.current?.focus();
    } else if (e.key === "ArrowRight") {
      e.preventDefault();
      confirmRef.current?.focus();
    }
  }

  return (
    <Modal open={open} onClose={onCancel} title={title}>
      <p className="text-[13px] font-semibold text-ink-soft">{message}</p>
      <div className="mt-5 flex justify-end gap-2" onKeyDown={onArrowKey}>
        <Button ref={cancelRef} variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
        <Button
          ref={confirmRef}
          variant={tone === "positive" ? "positive" : "danger"}
          onClick={onConfirm}
          disabled={busy}
        >
          {confirmLabel}
        </Button>
      </div>
    </Modal>
  );
}
